import { ReactNode, Suspense, useEffect, useState } from 'react'
import { Center, Loader, Text } from '@mantine/core'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { handleError } from '../common/error-handler'
import {
  Dashboard,
  DEFAULT_ROTATION_MS,
  GRID_COLS,
  GRID_ROWS,
  Slide,
} from '../admin/pages/dashboard/types'
import { getDashboardWidget } from '../admin/pages/dashboard/widgets/registry'
import classes from './Dashboard.module.css'
import { DashboardChrome } from './DashboardChrome'

const PROGRESS_TICK_MS = 100

// How long a failed widget stays blank before it retries on its own.
const WIDGET_RETRY_MS = 30000

interface RendererProps {
  config: Dashboard
}

export function DashboardRenderer({ config }: RendererProps) {
  const slides = config.slides
  const rotationMs = config.rotation_ms ?? DEFAULT_ROTATION_MS

  const [currentSlide, setCurrentSlide] = useState(0)
  const [progress, setProgress] = useState(0)

  // Slide rotation
  useEffect(() => {
    if (slides.length <= 1) return
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
      setProgress(0)
    }, rotationMs)
    return () => clearInterval(interval)
  }, [slides.length, rotationMs])

  // Progress bar tick
  useEffect(() => {
    const ticksPerSlide = rotationMs / PROGRESS_TICK_MS
    const interval = setInterval(() => {
      setProgress((prev) => Math.min(prev + 100 / ticksPerSlide, 100))
    }, PROGRESS_TICK_MS)
    return () => clearInterval(interval)
  }, [currentSlide, rotationMs])

  if (slides.length === 0) return null
  const slide = slides[Math.min(currentSlide, slides.length - 1)]

  return (
    <DashboardChrome
      slideCount={slides.length}
      currentSlide={currentSlide}
      slideProgress={progress}
      slideTitle={slide.title}
    >
      <SlideRender slide={slide} />
    </DashboardChrome>
  )
}

/**
 * Isolates one widget. Without this a single failing widget query throws past
 * Suspense to the nearest error boundary and takes the whole dashboard down -
 * on the TV that showed up as the dashboard and an error screen alternating
 * every rotation.
 */
function WidgetBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      FallbackComponent={WidgetErrorFallback}
      onError={handleError}
    >
      <Suspense
        fallback={
          <Center w="100%" h="100%">
            <Loader />
          </Center>
        }
      >
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

function WidgetErrorFallback({ resetErrorBoundary }: FallbackProps) {
  // Retry on the next rotation rather than immediately, so a persistently
  // broken widget does not spin.
  useEffect(() => {
    const timeout = setTimeout(resetErrorBoundary, WIDGET_RETRY_MS)
    return () => clearTimeout(timeout)
  }, [resetErrorBoundary])

  return (
    <Center w="100%" h="100%">
      <Text c="dimmed" size="sm">
        Unavailable
      </Text>
    </Center>
  )
}

function SlideRender({ slide }: { slide: Slide }) {
  return (
    <div
      className={classes.page}
      style={{
        gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
        gridTemplateRows: `repeat(${GRID_ROWS}, 1fr)`,
        gap: 'var(--mantine-spacing-xl)',
        gridAutoFlow: 'dense',
      }}
    >
      {slide.widgets.map((widget) => {
        const def = getDashboardWidget(widget.type)
        if (!def) return null
        const Component = def.Component
        return (
          <div
            key={widget.id}
            style={{
              gridColumn: `${widget.position.col + 1} / span ${widget.position.colSpan}`,
              gridRow: `${widget.position.row + 1} / span ${widget.position.rowSpan}`,
              minWidth: 0,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <WidgetBoundary>
              <Component
                settings={widget.settings as never}
                position={widget.position}
              />
            </WidgetBoundary>
          </div>
        )
      })}
    </div>
  )
}
